import React from 'react';
import { useDispatch } from 'react-redux';
import type { Artifact, ArtifactTemplate } from '../../api/artifacts/interface';
import { useSelector } from '../store';
import { actions as artifactsActions } from './logic';

interface ArtifactsHookState {
  artifacts: Artifact[];
  hasData: boolean;
  status: string;
}

interface ArtifactsHookHandler {
  fetchArtifacts: () => void;
  generateArtifact: (schemaName: string) => void;
}

const EMPTY: Artifact[] = [];

export default function useArtifactsHook(
  dossierId: string
): [ArtifactsHookState, ArtifactsHookHandler] {
  const dispatch = useDispatch();
  const artifacts = useSelector(
    (s) => s.artifacts.byDossierId[dossierId] ?? EMPTY
  );
  const hasData = useSelector(
    (s) => s.artifacts.byDossierId[dossierId] !== undefined
  );
  const status = useSelector(
    (s) => s.artifacts.statusByDossierId[dossierId] ?? 'uninitialized'
  );

  const handler = React.useMemo<ArtifactsHookHandler>(
    () => ({
      fetchArtifacts: () => {
        dispatch(artifactsActions.fetchArtifacts(dossierId));
      },
      generateArtifact: (schemaName: string) => {
        dispatch(artifactsActions.generateArtifact({ dossierId, schemaName }));
      },
    }),
    [dispatch, dossierId]
  );

  return [{ artifacts, hasData, status }, handler];
}

const EMPTY_TEMPLATES: ArtifactTemplate[] = [];

interface TemplatesHookState {
  templates: ArtifactTemplate[];
  hasData: boolean;
}

export function useArtifactTemplatesHook(
  dossierId: string
): [TemplatesHookState, { fetchTemplates: () => void }] {
  const dispatch = useDispatch();
  const templates = useSelector(
    (s) => s.artifacts.templatesByDossierId[dossierId] ?? EMPTY_TEMPLATES
  );
  const hasData = useSelector(
    (s) => s.artifacts.templatesByDossierId[dossierId] !== undefined
  );

  const handler = React.useMemo(
    () => ({
      fetchTemplates: () => {
        dispatch(artifactsActions.fetchArtifactTemplates(dossierId));
      },
    }),
    [dispatch, dossierId]
  );

  return [{ templates, hasData }, handler];
}
